import React, { useState, useEffect, useRef, useMemo } from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

const WordLadderGame = ({ roomId, gameState, gamePlayers = [], user = {}, isSpectator = false }) => {
  const socket = React.useContext(SocketContext);

  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
    perspectivePlayer,
  });

  const [guesses, setGuesses] = useState(["", "", ""]);
  const [feedback, setFeedback] = useState(null);
  const inputRefs = useRef([]);

  const players = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  const viewedPlayer = useMemo(() => {
    return players.find(matchesPerspectivePlayer) || null;
  }, [players, matchesPerspectivePlayer]);

  const viewedPlayerSocketId = useMemo(() => {
    const fromState = (gameState?.players || []).find(matchesPerspectivePlayer);
    if (fromState?.socketId) return fromState.socketId;
    if (viewedPlayer?.socketId) return viewedPlayer.socketId;
    if (socket?.id && !isSpectator) return socket.id;
    return perspectiveIds.find(Boolean) || null;
  }, [gameState?.players, matchesPerspectivePlayer, viewedPlayer?.socketId, socket?.id, isSpectator, perspectiveIds]);

  const finished = !!gameState?.finished;
  const winners = useMemo(() => (Array.isArray(gameState?.winners) ? gameState.winners : []), [gameState?.winners]);
  const result = useMemo(() => gameState?.result || null, [gameState?.result]);
  const firstWord = gameState?.firstWord || "";
  const lastWord = gameState?.lastWord || "";
  const correctChain = Array.isArray(gameState?.correctChain) ? gameState?.correctChain : [];
  const playerGuessedWords = useMemo(() => gameState?.playerGuessedWords || {}, [gameState?.playerGuessedWords]);
  const scores = useMemo(() => gameState?.scores || {}, [gameState?.scores]);

  // Determine how many middle words this puzzle requires (fall back to 3)
  const middleCount = correctChain.length >= 3 ? correctChain.length - 2 : 3;

  const myGuessedWords = useMemo(() => {
    return playerGuessedWords[viewedPlayerSocketId] || Array(middleCount).fill(false);
  }, [playerGuessedWords, viewedPlayerSocketId, middleCount]);

  const isYouWinner = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    if (viewedPlayerSocketId) idSet.add(String(viewedPlayerSocketId));
    return winners.some((id) => idSet.has(String(id)));
  }, [perspectiveIds, viewedPlayerSocketId, winners]);

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  // Auto-submit word when user presses Enter or completes typing
  const handleGuessChange = async (index, value) => {
    if (isSpectator) return;
    const normalized = value.toLowerCase();
    const updated = [...guesses];
    updated[index] = normalized;
    setGuesses(updated);

    // If word is already locked, don't let them change it anymore
    if (myGuessedWords[index]) {
      setGuesses((prev) => {
        const reset = [...prev];
        reset[index] = "";
        return reset;
      });
      return;
    }
  };

  const handleSubmitWord = async (index) => {
    if (isSpectator) return;
    if (myGuessedWords[index]) return; // Already locked
    if (!guesses[index].trim()) return;

    try {
      const ack = await submitAction({
        type: "guessWord",
        wordIndex: index,
        guess: guesses[index],
      });

      if (ack && ack.correct) {
        setFeedback("correct");
        setTimeout(() => setFeedback(null), 1500);

        // After a correct submission, focus the next unlocked clue (if any)
        setTimeout(() => {
          for (let i = index + 1; i < middleCount; i++) {
            if (!myGuessedWords[i]) {
              inputRefs.current[i]?.focus();
              return;
            }
          }
        }, 50);
      } else {
        setFeedback("incorrect");
        setGuesses((prev) => {
          const reset = [...prev];
          reset[index] = "";
          return reset;
        });
        setTimeout(() => setFeedback(null), 1500);
      }
    } catch (e) {
      setFeedback("error");
      setTimeout(() => setFeedback(null), 1500);
    }
  };

  const handleKeyDown = (e, index) => {
    if (isSpectator) return;
    if (e.key === "Enter") {
      handleSubmitWord(index);
    }
  };

  // Reset when game resets to a NEW puzzle
  useEffect(() => {
    if (!finished) {
      setGuesses(Array(middleCount).fill(""));
      setFeedback(null);
    }
  }, [gameState?.puzzle?.startWord, finished, middleCount]);

  useEffect(() => {
    setGuesses(Array(middleCount).fill(""));
    setFeedback(null);
  }, [viewedPlayerSocketId, middleCount]);

  if (!gameState) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-slate-900">
        <p className="text-lg text-gray-900 dark:text-gray-100">Loading game...</p>
      </div>
    );
  }

  if (finished && result) {
    // const isYouWinner = winners.some(
    //   (id) => id === socketId || id === user?.userId
    // );

    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 p-4 text-gray-900 dark:text-gray-100">
        <div className="max-w-xl mx-auto">
          {showSpectatorPerspectiveSelector && (
            <SpectatorPerspectiveSelector
              players={perspectiveOptions}
              value={spectatorViewPlayerId || ""}
              onChange={setSpectatorViewPlayerId}
              className="mb-4"
            />
          )}

          {/* Complete Word Ladder */}
          <div className="bg-gray-100 dark:bg-slate-800 rounded-lg shadow-lg p-4 mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 text-center">The Word Ladder</h3>
            <div className="flex flex-col gap-2">
              {correctChain.map((word, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-700 p-2 rounded text-gray-900 dark:text-gray-100 font-mono text-base text-center"
                >
                  {word.toUpperCase()}
                </div>
              ))}
            </div>
          </div>

          {/* Final Scores */}
          <div className="bg-gray-100 dark:bg-slate-800 rounded-lg shadow-lg p-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 text-center">Final Scores</h3>
            <div className="space-y-2">
              {players.map((player) => {
                const playerId = player.socketId || player.userId || player.id;
                const playerScore = scores[playerId] || 0;
                const isWinner = winners.includes(playerId);
                return (
                  <div
                    key={playerId}
                    className={`flex justify-between items-center p-2 rounded ${
                      isWinner
                        ? "bg-green-800 text-white font-bold"
                        : "bg-white dark:bg-slate-700"
                    }`}
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100">{player.username || "Player"} {isWinner && "👑"}</span>
                    <span className="font-semibold text-sm">{playerScore}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Back to Lobby Button */}
          <button
            onClick={() => window.location.reload()}
            className="w-full mt-6 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
        <div className="wordsearch-game p-4 text-gray-900 dark:text-gray-100">
      <div className="max-w-xl mx-auto">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
            className="mb-4"
          />
        )}

        {/* Main Game Area */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-6 mb-6">
          {/* Word Ladder */}
          <div className="flex flex-col gap-4 mb-8">
            {/* First Word (Given) */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-lg text-center">
              <p className="text-xs text-green-100 mb-1 font-semibold">START</p>
              <p className="text-2xl font-bold text-white">
                {firstWord.toUpperCase()}
              </p>
            </div>

            {/* Middle Words (To Guess) */}
            <div className="space-y-3">
              {Array.from({ length: middleCount }).map((_, index) => {
                const isLocked = myGuessedWords[index];
                const inputBgColor = isLocked
                  ? "bg-green-700 border-green-500 text-white"
                  : "bg-white border-slate-300 dark:bg-slate-700 dark:border-slate-600 text-gray-900 dark:text-gray-100";

                return (
                  <div key={index}>
                    <div className="flex gap-2 items-center" style={{ minWidth: 0 }}>
                      <input
                        id={`guess-${index}`}
                        type="text"
                        value={isLocked ? correctChain[index + 1]?.toUpperCase() || "" : guesses[index].toUpperCase()}
                        onChange={(e) => handleGuessChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        placeholder={`Enter word ${index + 2}`}
                        maxLength="10"
                        disabled={isLocked || isSpectator}
                        ref={(el) => (inputRefs.current[index] = el)}
                        autoComplete="off"
                        className={`min-w-0 flex-grow border p-1 sm:p-2 rounded text-center text-base sm:text-2xl font-bold font-mono tracking-wide sm:tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:cursor-not-allowed ${inputBgColor}`}
                        style={{ width: '100%' }}
                      />
                      {!isLocked && !isSpectator && (
                        <button
                          onClick={() => handleSubmitWord(index)}
                          className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-3 sm:py-2 sm:px-4 rounded transition flex items-center justify-center text-lg sm:text-xl"
                          style={{ minWidth: 36, minHeight: 36 }}
                        >
                          →
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Last Word (Given) */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-3 rounded-lg text-center">
              <p className="text-xs text-purple-100 mb-1 font-semibold">END</p>
              <p className="text-2xl font-bold text-white">
                {lastWord.toUpperCase()}
              </p>
            </div>
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              className={`text-center font-semibold p-2 rounded mb-3 ${
                feedback === "correct"
                  ? "bg-green-900 text-green-200"
                  : feedback === "incorrect"
                  ? "bg-red-900 text-red-200"
                  : "bg-yellow-900 text-yellow-200"
              }`}
            >
              {feedback === "correct" && "✓ Correct!"}
              {feedback === "incorrect" && "✗ Try again"}
              {feedback === "error" && "Error submitting"}
            </div>
          )}

          {finished && result?.message && (
            <div className={`text-center font-semibold p-2 rounded mb-3 ${isYouWinner ? "bg-green-900 text-green-200" : "bg-slate-700 text-slate-100"}`}>
              {result.message}
            </div>
          )}
        </div>

        {/* Player Progress */}
        <div className="bg-gray-100 dark:bg-slate-800 rounded-lg shadow-lg p-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">Players</h2>
          <div className="space-y-2">
              {players.map((player) => {
              const playerId = player.socketId || player.userId || player.id;
              const pGuessedWords = playerGuessedWords[playerId] || Array(middleCount).fill(false);
              const correct = pGuessedWords.filter((w) => w).length;
              const isViewedPlayer = [player.socketId, player.userId, player.id, player.username]
                .filter((value) => value !== undefined && value !== null)
                .some((value) => String(value) === String(viewedPlayerSocketId) || (perspectiveIds || []).map(String).includes(String(value)));
              return (
                <div
                  key={playerId}
                  className={`flex items-center justify-between p-2 rounded ${isViewedPlayer ? "bg-sky-100 dark:bg-sky-900/40 ring-1 ring-sky-300 dark:ring-sky-700" : "bg-white dark:bg-slate-700"}`}
                >
                  <span className="text-gray-900 dark:text-gray-100 font-semibold text-sm">
                    {player.username || "Unknown"}
                  </span>
                  <span
                    className={
                      correct === middleCount
                        ? "text-green-400 font-bold"
                        : "text-gray-400"
                    }
                  >
                    {correct}/{middleCount}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

WordLadderGame.Guide = function WordLadderGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Word Ladder</h3>
      <ul className="list-disc ml-6 space-y-1 text-sm">
        <li>You must find three middle words that form a word ladder.</li>
        <li>Each word differs from the next by exactly one letter.</li>
        <li>Type your guess in each field and click the arrow to submit.</li>
        <li>Although there are many combinations of words that can connect the start and end words, only one specific chain is the "correct" solution for this puzzle.</li> 
        <li>When correct, the word will lock and turn green.</li>
        <li>First player to find all three words wins!</li>
      </ul>
    </div>
  );
};

export default WordLadderGame;
